import type { CSSProperties, ElementType, HTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";
import { type ResponsiveProp, responsiveVars } from "@/lib/responsive";
import { cn } from "@/lib/utils";

export interface GridContainerProps extends HTMLAttributes<HTMLDivElement> {
  as?: ElementType;
  /** `grid-template-areas`. */
  areas?: ResponsiveProp<string>;
  /** `grid-template-rows`. */
  rows?: ResponsiveProp<string>;
  /** `grid-template-columns`. */
  columns?: ResponsiveProp<string>;
  autoRows?: ResponsiveProp<string>;
  autoColumns?: ResponsiveProp<string>;
  autoFlow?: CSSProperties["gridAutoFlow"];
  gap?: CSSProperties["gap"];
  gapX?: CSSProperties["columnGap"];
  gapY?: CSSProperties["rowGap"];
  alignItems?: CSSProperties["alignItems"];
  justifyContent?: CSSProperties["justifyContent"];
  children?: ReactNode;
}

/**
 * Grid CSS com valores por breakpoint.
 *
 * Cada prop responsiva vira uma custom property; a classe `.ds2-grid` do
 * `system.css` as lê numa cadeia de fallback, que é o que faz `md` herdar de
 * `sm` e `sm` de `base` sem depender de especificidade.
 */
export const GridContainer = forwardRef<HTMLDivElement, GridContainerProps>(
  (
    {
      as: Tag = "div",
      areas,
      rows,
      columns,
      autoRows,
      autoColumns,
      autoFlow,
      gap = 0,
      gapX,
      gapY,
      alignItems,
      justifyContent,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const computed: CSSProperties = {
      columnGap: gapX ?? gap,
      rowGap: gapY ?? gap,
      gridAutoFlow: autoFlow,
      alignItems,
      justifyContent,
      ...responsiveVars(areas, "gta"),
      ...responsiveVars(rows, "gtr"),
      ...responsiveVars(columns, "gtc"),
      ...responsiveVars(autoRows, "gar"),
      ...responsiveVars(autoColumns, "gac"),
    };

    return (
      <Tag
        ref={ref}
        className={cn("ds2-grid", className)}
        style={{ ...computed, ...style }}
        {...props}
      >
        {children}
      </Tag>
    );
  },
);
GridContainer.displayName = "GridContainer";

export interface GridItemProps extends HTMLAttributes<HTMLDivElement> {
  as?: ElementType;
  /** `grid-column`, ex.: `"span 2"` ou `"1 / 3"`. */
  col?: ResponsiveProp<string>;
  /** `grid-row`. */
  row?: ResponsiveProp<string>;
  /** Nome da área declarada em `areas` do container. */
  area?: string;
  alignSelf?: CSSProperties["alignSelf"];
  justifySelf?: CSSProperties["justifySelf"];
  children?: ReactNode;
}

/** Um item posicionado dentro de um `GridContainer`. */
export const GridItem = forwardRef<HTMLDivElement, GridItemProps>(
  (
    {
      as: Tag = "div",
      col,
      row,
      area,
      alignSelf,
      justifySelf,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const computed: CSSProperties = {
      gridArea: area,
      alignSelf,
      justifySelf,
      ...responsiveVars(col, "gcol"),
      ...responsiveVars(row, "grow"),
    };

    return (
      <Tag
        ref={ref}
        className={cn("ds2-grid-item", className)}
        style={{ ...computed, ...style }}
        {...props}
      >
        {children}
      </Tag>
    );
  },
);
GridItem.displayName = "GridItem";
